import { useCallback, useEffect, useMemo, useState } from 'react';
import { createSnapshotTag, listSnapshotTags } from 'src/components/star-grid/services/starGridService';
import type { CreateSnapshotTagRequest, SnapshotTagRow } from 'src/components/star-grid/starGridTypes';

/**
 * Snapshot Manager state: every snapshot of the alpha fact, and tagging one.
 *
 * Reloads after a tag is created rather than inserting the row locally: the tag lives in the table's
 * own refs, and only the server can say it landed.
 */
export const useSnapshotManager = () => {
  const [snapshots, setSnapshots] = useState<readonly SnapshotTagRow[]>([]);
  const [prefixes, setPrefixes] = useState<readonly string[]>([]);
  const [periodFormats, setPeriodFormats] = useState<Readonly<Record<string, string>>>({});
  const [table, setTable] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const list = await listSnapshotTags(signal);
      if (!signal?.aborted) {
        setSnapshots(list.snapshots);
        setPrefixes(list.prefixes);
        setPeriodFormats(list.period_formats ?? {});
        setTable(list.table);
        setError(undefined);
      }
    } catch (caught) {
      if (!signal?.aborted) setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return snapshots;
    return snapshots.filter((row) =>
      [row.label, row.tag, row.description, row.created_by, row.snapshot_id].some((value) => (value ?? '').toLowerCase().includes(query))
    );
  }, [snapshots, search]);

  const create = useCallback(
    async (request: CreateSnapshotTagRequest): Promise<boolean> => {
      setCreating(true);
      try {
        await createSnapshotTag(request);
        await load();
        return true;
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
        return false;
      } finally {
        setCreating(false);
      }
    },
    [load]
  );

  return {
    snapshots: visible,
    latest: snapshots.find((row) => !row.tagged) ?? snapshots[0],
    total: snapshots.length,
    prefixes,
    periodFormats,
    table,
    loading,
    error,
    creating,
    search,
    setSearch,
    refresh: () => void load(),
    create
  };
};
